import {Label} from '@/base/ui/label'
import {cn, randomId} from '@/lib/utils'
import {cva} from 'class-variance-authority'
import React from 'react'
import {Switch} from '../../base/ui/switch'

export type IWuToggleProps = Omit<
  React.ComponentPropsWithRef<typeof Switch>,
  'onChange'
> & {
  label?: string
  labelPosition?: 'left' | 'right' | 'top'
  onChange?: (e: boolean) => void
}

export const WuToggle = React.forwardRef<HTMLButtonElement, IWuToggleProps>(
  (props, ref): React.JSX.Element => {
    const {
      label,
      labelPosition = 'right',
      onChange,
      className,
      dir,
      disabled,
      id = randomId(),
      ...rest
    } = props

    React.useEffect(() => {
      const {checked, defaultChecked} = rest
      if (checked !== undefined && defaultChecked !== undefined) {
        console.error(
          'WuToggle: Both `checked` and `defaultChecked` props are set. Use only one of them.',
        )
      }
    }, [rest.checked, rest.defaultChecked])

    return (
      <div
        className={cn(
          labelPositionClasses({labelPosition}),
          'wu-toggle-container',
        )}
        dir={dir}
      >
        <Switch
          {...rest}
          id={id}
          disabled={disabled}
          ref={ref}
          onCheckedChange={onChange}
          className={cn('peer', className)}
        />
        <Label
          htmlFor={id}
          className={cn(
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            'wu-toggle-label',
          )}
        >
          {label}
        </Label>
      </div>
    )
  },
)

WuToggle.displayName = 'WuToggle'

const labelPositionClasses = cva('flex items-center gap-3 w-fit', {
  variants: {
    labelPosition: {
      right: 'flex-row',
      left: 'flex-row-reverse',
      top: 'flex-col-reverse items-baseline',
    },
  },
})
